/**
 * Inspection Form Utilities
 *
 * Provides conversion functions between Inspection entities and FormGroups.
 * These utilities support both check-in and DVI mobile components by providing
 * a shared way to create and extract FormGroups from Inspection entities.
 *
 * Supports polymorphic inspection items with type discrimination based on itemType.
 */
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import {
    CheckboxInspectionItem,
    CheckboxInspectionItemFormGroup,
    DateInputInspectionItem,
    DateInputInspectionItemFormGroup,
    FreehandNoteEntry,
    FreehandNoteEntryFormGroup,
    Inspection,
    InspectionFormGroup,
    InspectionItem,
    InspectionItemAttachment,
    InspectionItemAttachmentFormGroup,
    InspectionItemBase,
    InspectionItemBinding,
    InspectionItemFormGroup,
    InspectionItemFormGroupBase,
    InspectionItemResult,
    InspectionItemResultFormGroup,
    InspectionItemResultType,
    InspectionItemType,
    InspectionSection,
    InspectionSectionFilter,
    InspectionSectionFilterFormGroup,
    InspectionSectionFormGroup,
    NotesInputType,
    NotesInspectionItem,
    NotesInspectionItemFormGroup,
    NumericRangeInspectionItem,
    NumericRangeInspectionItemFormGroup,
    TrafficLightInspectionItem,
    TrafficLightInspectionItemFormGroup,
    TriStateInspectionItem,
    TriStateInspectionItemFormGroup,
    TriStateValue,
    UnknownInspectionItemFormGroup,
    Vehicle,
} from '../models';

/**
 * Flattens a section tree into a single list holding both the given sections and
 * every nested child section. Use this before looking at items or attachments so
 * nothing inside a sub-section gets skipped.
 */
export function collectInspectionSections(sections: InspectionSection[] | undefined): InspectionSection[] {
    const flattened: InspectionSection[] = [];
    const visit = (current: InspectionSection[] | undefined): void => {
        for (const section of current ?? []) {
            flattened.push(section);
            visit(section.childSections);
        }
    };
    visit(sections);
    return flattened;
}

/**
 * Creates a FormGroup from an Inspection entity.
 * Preserves all properties and handles nested structures.
 *
 * @param inspection - The inspection entity to convert
 * @returns FormGroup matching InspectionFormGroup interface
 */
export function createInspectionFormGroup(inspection: Inspection): FormGroup<InspectionFormGroup> {
    return new FormGroup<InspectionFormGroup>({
        advisorId: new FormControl<string>(inspection.advisorId || '', { nonNullable: true }),
        customerNotes: new FormControl<string | null>(inspection.customerNotes || null),
        isLocked: new FormControl<boolean>(inspection.isLocked || false, { nonNullable: true }),
        sections: new FormArray<FormGroup<InspectionSectionFormGroup>>(inspection.sections?.map((section) => createSectionFormGroup(section)) || []),
        technicianId: new FormControl<string | null>(inspection.technicianId || null),
    });
}

/**
 * Extracts an Inspection entity from a FormGroup.
 * Merges FormGroup values back into the original inspection structure.
 *
 * @param formGroup - The FormGroup to extract values from
 * @param originalInspection - Original inspection to preserve non-form properties
 * @returns Updated Inspection entity
 */
export function extractInspectionFromFormGroup(formGroup: FormGroup<InspectionFormGroup>, originalInspection: Inspection): Inspection {
    return {
        ...originalInspection,
        advisorId: formGroup.controls.advisorId.value,
        customerNotes: formGroup.controls.customerNotes?.value || undefined,
        isLocked: formGroup.controls.isLocked.value,
        sections: formGroup.controls.sections.controls.map((sectionFormGroup, index) => {
            const originalSection = originalInspection.sections[index];
            return extractSectionFromFormGroup(sectionFormGroup, originalSection);
        }),
        technicianId: formGroup.controls.technicianId?.value || undefined,
    };
}

/**
 * Creates a FormGroup from an InspectionSection entity.
 *
 * @param section - The section entity to convert
 * @returns FormGroup matching InspectionSectionFormGroup interface
 */
export function createSectionFormGroup(section: InspectionSection): FormGroup<InspectionSectionFormGroup> {
    return new FormGroup<InspectionSectionFormGroup>({
        id: new FormControl<string>(section.id, { nonNullable: true }),
        branchId: new FormControl<string>(section.branchId, { nonNullable: true }),
        tenantId: new FormControl<string>(section.tenantId, { nonNullable: true }),
        inspectionId: new FormControl<string>(section.inspectionId, { nonNullable: true }),
        name: new FormControl<string>(section.name, { nonNullable: true }),
        order: new FormControl<number>(section.order, { nonNullable: true }),
        parentSectionId: new FormControl<string | null>(section.parentSectionId ?? null),
        childSections: new FormArray<FormGroup<InspectionSectionFormGroup>>(
            section.childSections?.map((childSection) => createSectionFormGroup(childSection)) || [],
        ),
        items: new FormArray<FormGroup<InspectionItemFormGroup>>(
            section.items?.map((item) => createItemFormGroup(item) as FormGroup<InspectionItemFormGroup>) || [],
        ),
        filter: createSectionFilterFormGroup(section.filter),
    });
}

/**
 * Creates a FormGroup for section filter.
 *
 * @param filter - The filter object (optional)
 * @returns FormGroup matching InspectionSectionFilterFormGroup interface
 */
function createSectionFilterFormGroup(filter?: InspectionSectionFilter<InspectionItemBase>): FormGroup<InspectionSectionFilterFormGroup> {
    return new FormGroup<InspectionSectionFilterFormGroup>({
        name: new FormControl<string | null>(filter?.name || null),
        property: new FormControl<keyof InspectionItemBase | null>(filter?.property || null),
        value: new FormControl<string | boolean | number | null>(filter?.value || null),
    });
}

/**
 * Extracts an InspectionSection from a FormGroup.
 *
 * @param sectionFormGroup - The section FormGroup
 * @param originalSection - Original section to preserve non-form properties
 * @returns Updated InspectionSection
 */
function extractSectionFromFormGroup(sectionFormGroup: FormGroup<InspectionSectionFormGroup>, originalSection: InspectionSection): InspectionSection {
    return {
        ...originalSection,
        id: sectionFormGroup.controls.id.value,
        branchId: sectionFormGroup.controls.branchId.value,
        tenantId: sectionFormGroup.controls.tenantId.value,
        inspectionId: sectionFormGroup.controls.inspectionId.value,
        name: sectionFormGroup.controls.name.value,
        order: sectionFormGroup.controls.order.value,
        items: sectionFormGroup.controls.items.controls.map((itemFormGroup, index) => {
            const originalItem = originalSection.items[index];
            return extractItemFromFormGroup(itemFormGroup, originalItem);
        }),
        filter: {
            name: sectionFormGroup.controls.filter.controls.name.value,
            property: sectionFormGroup.controls.filter.controls.property.value,
            value: sectionFormGroup.controls.filter.controls.value.value,
        },
    };
}

/**
 * Creates a FormGroup from an InspectionItem entity.
 * Discriminates based on itemType to create the appropriate specific form group.
 *
 * @param item - The item entity to convert
 * @returns FormGroup matching the specific InspectionItemFormGroup variant
 */
export function createItemFormGroup(
    item: InspectionItem,
):
    | FormGroup<TrafficLightInspectionItemFormGroup>
    | FormGroup<NumericRangeInspectionItemFormGroup>
    | FormGroup<CheckboxInspectionItemFormGroup>
    | FormGroup<NotesInspectionItemFormGroup>
    | FormGroup<DateInputInspectionItemFormGroup>
    | FormGroup<TriStateInspectionItemFormGroup>
    | FormGroup<UnknownInspectionItemFormGroup> {
    switch (item.itemType) {
        case InspectionItemType.TrafficLight:
            return createTrafficLightItemFormGroup(item as TrafficLightInspectionItem);
        case InspectionItemType.NumericRange:
            return createNumericRangeItemFormGroup(item as NumericRangeInspectionItem);
        case InspectionItemType.Checkbox:
            return createCheckboxItemFormGroup(item as CheckboxInspectionItem);
        case InspectionItemType.Notes:
            return createNotesItemFormGroup(item as NotesInspectionItem);
        case InspectionItemType.DateInput:
            return createDateInputItemFormGroup(item as DateInputInspectionItem);
        case InspectionItemType.TriState:
            return createTriStateItemFormGroup(item as TriStateInspectionItem);
        default:
            return createUnknownItemFormGroup(item);
    }
}

/**
 * Creates base form controls common to all inspection item types.
 *
 * @param item - The base item entity
 * @returns Object with base form controls
 */
export function createBaseItemControls(item: InspectionItemBase): InspectionItemFormGroupBase {
    return {
        id: new FormControl<string>(item.id, { nonNullable: true }),
        branchId: new FormControl<string>(item.branchId, { nonNullable: true }),
        tenantId: new FormControl<string>(item.tenantId, { nonNullable: true }),
        sectionId: new FormControl<string>(item.sectionId, { nonNullable: true }),
        templateItemId: new FormControl<string | null>(item.templateItemId ?? null),
        name: new FormControl<string>(item.name, { nonNullable: true }),
        order: new FormControl<number>(item.order, { nonNullable: true }),
        isPhotoRequired: new FormControl<boolean>(item.isPhotoRequired, { nonNullable: true }),
        isVideoRequired: new FormControl<boolean>(item.isVideoRequired, { nonNullable: true }),
        isRequired: new FormControl<boolean>(item.isRequired, { nonNullable: true }),
        isHidden: new FormControl<boolean>(item.isHidden, { nonNullable: true }),
        itemType: new FormControl<InspectionItemType>(item.itemType, { nonNullable: true }),
        binding: new FormControl<InspectionItemBinding>(item.binding ?? InspectionItemBinding.None, { nonNullable: true }),
        knowledgeHintDescription: new FormControl<string | null>(item.knowledgeHintDescription || null),
        knowledgeHintImpact: new FormControl<string | null>(item.knowledgeHintImpact || null),
        inspectionId: new FormControl<string>(item.inspectionId, { nonNullable: true }),
        notes: new FormArray<FormControl<string | null>>(item.notes?.map((note) => new FormControl<string | null>(note)) || []),
        recommendations: new FormArray<FormControl<string | null>>(item.recommendations?.map((rec) => new FormControl<string | null>(rec)) || []),
        freehandEntries: new FormArray<FormGroup<FreehandNoteEntryFormGroup>>(
            item.freehandEntries?.map((entry) => createFreehandNoteEntryFormGroup(entry)) || [],
        ),
        attachments: new FormArray<FormGroup<InspectionItemAttachmentFormGroup>>(
            item.attachments?.map((att) => createAttachmentFormGroup(att)) || [],
        ),
    };
}

/**
 * Builds a typed FormGroup for a single freehand entry.
 *
 * Exposed so callers (e.g. the mobile inspection-note-options picker) can append
 * new entries to an existing item's `freehandEntries` form array as the tech
 * authors them, without rebuilding the entire item form group.
 */
export function createFreehandNoteEntryFormGroup(entry: FreehandNoteEntry): FormGroup<FreehandNoteEntryFormGroup> {
    return new FormGroup<FreehandNoteEntryFormGroup>({
        note: new FormControl<string>(entry.note, { nonNullable: true }),
        recommendation: new FormControl<string | null>(entry.recommendation ?? null),
    });
}

/**
 * Creates a FormGroup for a TrafficLight inspection item.
 */
function createTrafficLightItemFormGroup(item: TrafficLightInspectionItem): FormGroup<TrafficLightInspectionItemFormGroup> {
    const formGroup = new FormGroup<TrafficLightInspectionItemFormGroup>({
        ...createBaseItemControls(item),
        itemType: new FormControl<InspectionItemType.TrafficLight>(InspectionItemType.TrafficLight, { nonNullable: true }),
        resultId: new FormControl<string>(item.resultId ?? null, { nonNullable: true }),
        allowPass: new FormControl<boolean>(item.allowPass, { nonNullable: true }),
        allowWarning: new FormControl<boolean>(item.allowWarning, { nonNullable: true }),
        allowFail: new FormControl<boolean>(item.allowFail, { nonNullable: true }),
        passLabel: new FormControl<string>(item.passLabel || 'Pass', { nonNullable: true }),
        warningLabel: new FormControl<string>(item.warningLabel || 'Warning', { nonNullable: true }),
        failLabel: new FormControl<string>(item.failLabel || 'Fail', { nonNullable: true }),
        quoteItemId: new FormControl<string | null>(item.quoteItemId ?? null),
        ...(item.result ? { result: createResultFormGroup(item.result) } : {}),
    } as TrafficLightInspectionItemFormGroup);

    // Add required validator if item is required
    if (item.isRequired) {
        formGroup.controls.resultId.addValidators([Validators.required]);
    }

    return formGroup;
}

/**
 * Creates a FormGroup for a NumericRange inspection item.
 */
function createNumericRangeItemFormGroup(item: NumericRangeInspectionItem): FormGroup<NumericRangeInspectionItemFormGroup> {
    const formGroup = new FormGroup<NumericRangeInspectionItemFormGroup>({
        ...createBaseItemControls(item),
        itemType: new FormControl<InspectionItemType.NumericRange>(InspectionItemType.NumericRange, { nonNullable: true }),
        actualValue: new FormControl<number | null>(item.actualValue),
        minValue: new FormControl<number>(item.minValue, { nonNullable: true }),
        maxValue: new FormControl<number>(item.maxValue, { nonNullable: true }),
        unit: new FormControl<string>(item.unit, { nonNullable: true }),
        decimalPlaces: new FormControl<number>(item.decimalPlaces, { nonNullable: true }),
        constraints: new FormControl(item.constraints, { nonNullable: true }),
        passLabel: new FormControl(item.passLabel ?? null),
        warningLabel: new FormControl(item.warningLabel ?? null),
        failLabel: new FormControl(item.failLabel ?? null),
        resultId: new FormControl(item.resultId ?? null),
        quoteItemId: new FormControl<string | null>(item.quoteItemId ?? null),
    });

    // Add required validator if item is required
    if (item.isRequired) {
        formGroup.controls.actualValue.addValidators([Validators.required]);
    }

    return formGroup;
}

/**
 * Creates a FormGroup for a Checkbox inspection item.
 */
function createCheckboxItemFormGroup(item: CheckboxInspectionItem): FormGroup<CheckboxInspectionItemFormGroup> {
    const formGroup = new FormGroup<CheckboxInspectionItemFormGroup>({
        ...createBaseItemControls(item),
        itemType: new FormControl<InspectionItemType.Checkbox>(InspectionItemType.Checkbox, { nonNullable: true }),
        value: new FormControl<boolean | null>(item.value ?? false),
        trueLabel: new FormControl<string>(item.trueLabel, { nonNullable: true }),
        falseLabel: new FormControl<string>(item.falseLabel, { nonNullable: true }),
    });

    // Add required validator if item is required
    if (item.isRequired) {
        formGroup.controls.value.addValidators([Validators.required]);
    }

    return formGroup;
}

/**
 * Creates a base-only FormGroup for an item type this build does not recognise.
 * An inspection carrying a newer item type has to stay openable on an older client, so the unknown
 * item is preserved rather than skipped and its answer is left for a build that understands it.
 */
function createUnknownItemFormGroup(item: InspectionItem): FormGroup<UnknownInspectionItemFormGroup> {
    return new FormGroup<UnknownInspectionItemFormGroup>({
        ...createBaseItemControls(item),
        itemType: new FormControl<InspectionItemType>(item.itemType, { nonNullable: true }),
    });
}

/**
 * Creates a FormGroup for a TriState inspection item.
 */
function createTriStateItemFormGroup(item: TriStateInspectionItem): FormGroup<TriStateInspectionItemFormGroup> {
    const formGroup = new FormGroup<TriStateInspectionItemFormGroup>({
        ...createBaseItemControls(item),
        itemType: new FormControl<InspectionItemType.TriState>(InspectionItemType.TriState, { nonNullable: true }),
        // Never defaulted: an untouched item must stay distinguishable from a deliberate NotApplicable
        value: new FormControl<TriStateValue | null>(item.value ?? null),
        positiveLabel: new FormControl<string>(item.positiveLabel, { nonNullable: true }),
        middleLabel: new FormControl<string>(item.middleLabel, { nonNullable: true }),
        negativeLabel: new FormControl<string>(item.negativeLabel, { nonNullable: true }),
    });

    if (item.isRequired) {
        formGroup.controls.value.addValidators([Validators.required]);
        // addValidators does not re-run validation, so an untouched required item would report valid
        formGroup.controls.value.updateValueAndValidity({ emitEvent: false });
    }

    return formGroup;
}

/**
 * Creates a FormGroup for a Notes inspection item.
 */
function createNotesItemFormGroup(item: NotesInspectionItem): FormGroup<NotesInspectionItemFormGroup> {
    const formGroup = new FormGroup<NotesInspectionItemFormGroup>({
        ...createBaseItemControls(item),
        itemType: new FormControl<InspectionItemType.Notes>(InspectionItemType.Notes, { nonNullable: true }),
        content: new FormControl<string | null>(item.content),
        placeholder: new FormControl<string>(item.placeholder || '', { nonNullable: true }),
        maxLength: new FormControl<number | null>(item.maxLength || null),
        inputType: new FormControl<NotesInputType>(item.inputType, { nonNullable: true }),
    });

    // Add required validator if item is required
    if (item.isRequired) {
        formGroup.controls.content.addValidators([Validators.required]);
    }

    return formGroup;
}

/**
 * Creates a FormGroup for a DateInput inspection item.
 */
function createDateInputItemFormGroup(item: DateInputInspectionItem): FormGroup<DateInputInspectionItemFormGroup> {
    const formGroup = new FormGroup<DateInputInspectionItemFormGroup>({
        ...createBaseItemControls(item),
        itemType: new FormControl<InspectionItemType.DateInput>(InspectionItemType.DateInput, { nonNullable: true }),
        dateValue: new FormControl<string | null>(item.dateValue),
        placeholder: new FormControl<string>(item.placeholder || '', { nonNullable: true }),
        minDate: new FormControl<string | null>(item.minDate || null),
        maxDate: new FormControl<string | null>(item.maxDate || null),
        resultId: new FormControl<string | null>(item.resultId ?? null),
    });

    // Add required validator if item is required
    if (item.isRequired) {
        formGroup.controls.dateValue.addValidators([Validators.required]);
    }

    return formGroup;
}

/**
 * Creates a FormGroup from an InspectionItemAttachment entity.
 *
 * @param attachment - The attachment entity to convert
 * @returns FormGroup matching InspectionItemAttachmentFormGroup interface
 */
export function createAttachmentFormGroup(attachment: InspectionItemAttachment): FormGroup<InspectionItemAttachmentFormGroup> {
    return new FormGroup<InspectionItemAttachmentFormGroup>({
        id: new FormControl<string | null>(attachment.id || null),
        branchId: new FormControl<string>(attachment.branchId, { nonNullable: true }),
        tenantId: new FormControl<string>(attachment.tenantId, { nonNullable: true }),
        itemId: new FormControl<string>(attachment.itemId, { nonNullable: true }),
        createdDate: new FormControl<string>(attachment.createdDate || new Date().toISOString(), { nonNullable: true }),
        contentBlob: new FormControl<Blob | null | undefined>(attachment.contentBlob || null),
        internalName: new FormControl<string | null | undefined>(attachment.internalName || null),
        name: new FormControl<string | null | undefined>(attachment.name || null),
        mimeType: new FormControl<string | null | undefined>(attachment.mimeType || null),
        url: new FormControl<string | null | undefined>(attachment.url || null),
        isLoading: new FormControl<boolean | null>(attachment.isLoading || null),
    });
}

/**
 * Creates a FormGroup from an InspectionItemResult entity.
 *
 * @param result - The result entity to convert
 * @returns FormGroup matching InspectionItemResultFormGroup interface
 */
function createResultFormGroup(result: InspectionItemResult): FormGroup<InspectionItemResultFormGroup> {
    return new FormGroup<InspectionItemResultFormGroup>({
        id: new FormControl<string>(result.id, { nonNullable: true }),
        branchId: new FormControl<string>(result.branchId, { nonNullable: true }),
        tenantId: new FormControl<string>(result.tenantId, { nonNullable: true }),
        name: new FormControl<string>(result.name, { nonNullable: true }),
    });
}

/**
 * Extracts an InspectionItem from a FormGroup.
 * Discriminates based on itemType to extract the appropriate specific item type.
 *
 * @param itemFormGroup - The item FormGroup
 * @param originalItem - Original item to preserve non-form properties
 * @returns Updated InspectionItem with correct type
 */
function extractItemFromFormGroup(itemFormGroup: FormGroup<InspectionItemFormGroup>, originalItem: InspectionItem): InspectionItem {
    const itemType = itemFormGroup.controls.itemType.value;

    switch (itemType) {
        case InspectionItemType.TrafficLight:
            return extractTrafficLightItemFromFormGroup(
                itemFormGroup as FormGroup<TrafficLightInspectionItemFormGroup>,
                originalItem as TrafficLightInspectionItem,
            );
        case InspectionItemType.NumericRange:
            return extractNumericRangeItemFromFormGroup(
                itemFormGroup as FormGroup<NumericRangeInspectionItemFormGroup>,
                originalItem as NumericRangeInspectionItem,
            );
        case InspectionItemType.Checkbox:
            return extractCheckboxItemFromFormGroup(
                itemFormGroup as FormGroup<CheckboxInspectionItemFormGroup>,
                originalItem as CheckboxInspectionItem,
            );
        case InspectionItemType.Notes:
            return extractNotesItemFromFormGroup(itemFormGroup as FormGroup<NotesInspectionItemFormGroup>, originalItem as NotesInspectionItem);
        case InspectionItemType.DateInput:
            return extractDateInputItemFromFormGroup(
                itemFormGroup as FormGroup<DateInputInspectionItemFormGroup>,
                originalItem as DateInputInspectionItem,
            );
        case InspectionItemType.TriState:
            return extractTriStateItemFromFormGroup(
                itemFormGroup as FormGroup<TriStateInspectionItemFormGroup>,
                originalItem as TriStateInspectionItem,
            );
        default:
            // The form carries no controls for an unrecognised type, so the stored item is the only
            // truthful source for it -- returning it unchanged keeps a newer type's answer intact.
            return originalItem;
    }
}

/**
 * Extracts base properties common to all inspection item types.
 */
function extractBaseItemProperties(
    itemFormGroup:
        | FormGroup<TrafficLightInspectionItemFormGroup>
        | FormGroup<NumericRangeInspectionItemFormGroup>
        | FormGroup<CheckboxInspectionItemFormGroup>
        | FormGroup<NotesInspectionItemFormGroup>
        | FormGroup<DateInputInspectionItemFormGroup>
        | FormGroup<TriStateInspectionItemFormGroup>,
    originalItem: InspectionItemBase,
): InspectionItemBase {
    return {
        ...originalItem,
        id: itemFormGroup.controls.id.value,
        branchId: itemFormGroup.controls.branchId.value,
        tenantId: itemFormGroup.controls.tenantId.value,
        sectionId: itemFormGroup.controls.sectionId.value,
        name: itemFormGroup.controls.name.value,
        order: itemFormGroup.controls.order.value,
        isPhotoRequired: itemFormGroup.controls.isPhotoRequired.value,
        isVideoRequired: itemFormGroup.controls.isVideoRequired.value,
        isRequired: itemFormGroup.controls.isRequired.value,
        isHidden: itemFormGroup.controls.isHidden.value,
        itemType: itemFormGroup.controls.itemType.value,
        knowledgeHintDescription: itemFormGroup.controls.knowledgeHintDescription?.value || null,
        knowledgeHintImpact: itemFormGroup.controls.knowledgeHintImpact?.value || null,
        notes: itemFormGroup.controls.notes.controls.map((c: FormControl<string | null>) => c.value).filter((n): n is string => n !== null),
        recommendations: itemFormGroup.controls.recommendations.controls
            .map((c: FormControl<string | null>) => c.value)
            .filter((r): r is string => r !== null),
        attachments: itemFormGroup.controls.attachments.controls.map((attFormGroup: FormGroup<InspectionItemAttachmentFormGroup>) =>
            extractAttachmentFromFormGroup(attFormGroup),
        ),
        ...extractFreehandEntries(itemFormGroup.controls.freehandEntries),
    };
}

/**
 * Extracts the optional `freehandEntries` sidecar. Returned as a spreadable partial so
 * callers omit the field entirely when no entries exist -- this keeps the round-trip
 * output identical to inputs that never carried the field.
 */
function extractFreehandEntries(
    freehandEntries: FormArray<FormGroup<FreehandNoteEntryFormGroup>>,
): Partial<Pick<InspectionItemBase, 'freehandEntries'>> {
    if (freehandEntries.length === 0) return {};
    return {
        freehandEntries: freehandEntries.controls.map((entryGroup: FormGroup<FreehandNoteEntryFormGroup>): FreehandNoteEntry => ({
            note: entryGroup.controls.note.value,
            recommendation: entryGroup.controls.recommendation.value,
        })),
    };
}

/**
 * Extracts a TrafficLight inspection item from FormGroup.
 */
function extractTrafficLightItemFromFormGroup(
    itemFormGroup: FormGroup<TrafficLightInspectionItemFormGroup>,
    originalItem: TrafficLightInspectionItem,
): TrafficLightInspectionItem {
    return {
        ...extractBaseItemProperties(itemFormGroup, originalItem),
        itemType: InspectionItemType.TrafficLight,
        resultId: itemFormGroup.controls.resultId.value,
        allowPass: itemFormGroup.controls.allowPass.value,
        allowWarning: itemFormGroup.controls.allowWarning.value,
        allowFail: itemFormGroup.controls.allowFail.value,
        passLabel: itemFormGroup.controls.passLabel.value,
        warningLabel: itemFormGroup.controls.warningLabel.value,
        failLabel: itemFormGroup.controls.failLabel.value,
        result: itemFormGroup.controls.result ? extractResultFromFormGroup(itemFormGroup.controls.result) : undefined,
    } as TrafficLightInspectionItem;
}

/**
 * Extracts a NumericRange inspection item from FormGroup.
 */
function extractNumericRangeItemFromFormGroup(
    itemFormGroup: FormGroup<NumericRangeInspectionItemFormGroup>,
    originalItem: NumericRangeInspectionItem,
): NumericRangeInspectionItem {
    return {
        ...extractBaseItemProperties(itemFormGroup, originalItem),
        itemType: InspectionItemType.NumericRange,
        actualValue: itemFormGroup.controls.actualValue.value,
        minValue: itemFormGroup.controls.minValue.value,
        maxValue: itemFormGroup.controls.maxValue.value,
        unit: itemFormGroup.controls.unit.value,
        decimalPlaces: itemFormGroup.controls.decimalPlaces.value,
        constraints: itemFormGroup.controls.constraints.value,
        passLabel: itemFormGroup.controls.passLabel.value,
        warningLabel: itemFormGroup.controls.warningLabel.value,
        failLabel: itemFormGroup.controls.failLabel.value,
        resultId: itemFormGroup.controls.resultId.value,
    } as NumericRangeInspectionItem;
}

/**
 * Extracts a Checkbox inspection item from FormGroup.
 */
function extractCheckboxItemFromFormGroup(
    itemFormGroup: FormGroup<CheckboxInspectionItemFormGroup>,
    originalItem: CheckboxInspectionItem,
): CheckboxInspectionItem {
    return {
        ...extractBaseItemProperties(itemFormGroup, originalItem),
        itemType: InspectionItemType.Checkbox,
        value: itemFormGroup.controls.value.value,
        trueLabel: itemFormGroup.controls.trueLabel.value,
        falseLabel: itemFormGroup.controls.falseLabel.value,
    } as CheckboxInspectionItem;
}

/**
 * Extracts a Notes inspection item from FormGroup.
 */
function extractNotesItemFromFormGroup(
    itemFormGroup: FormGroup<NotesInspectionItemFormGroup>,
    originalItem: NotesInspectionItem,
): NotesInspectionItem {
    return {
        ...extractBaseItemProperties(itemFormGroup, originalItem),
        itemType: InspectionItemType.Notes,
        content: itemFormGroup.controls.content.value,
        placeholder: itemFormGroup.controls.placeholder.value,
        maxLength: itemFormGroup.controls.maxLength.value,
    } as NotesInspectionItem;
}

/**
 * Extracts a DateInput inspection item from FormGroup.
 */
function extractDateInputItemFromFormGroup(
    itemFormGroup: FormGroup<DateInputInspectionItemFormGroup>,
    originalItem: DateInputInspectionItem,
): DateInputInspectionItem {
    return {
        ...extractBaseItemProperties(itemFormGroup, originalItem),
        itemType: InspectionItemType.DateInput,
        dateValue: itemFormGroup.controls.dateValue.value,
        placeholder: itemFormGroup.controls.placeholder.value,
        minDate: itemFormGroup.controls.minDate.value,
        maxDate: itemFormGroup.controls.maxDate.value,
        resultId: itemFormGroup.controls.resultId.value,
    } as DateInputInspectionItem;
}

/**
 * Extracts a TriState inspection item from FormGroup.
 */
function extractTriStateItemFromFormGroup(
    itemFormGroup: FormGroup<TriStateInspectionItemFormGroup>,
    originalItem: TriStateInspectionItem,
): TriStateInspectionItem {
    return {
        ...extractBaseItemProperties(itemFormGroup, originalItem),
        itemType: InspectionItemType.TriState,
        value: itemFormGroup.controls.value.value,
        positiveLabel: itemFormGroup.controls.positiveLabel.value,
        middleLabel: itemFormGroup.controls.middleLabel.value,
        negativeLabel: itemFormGroup.controls.negativeLabel.value,
    } as TriStateInspectionItem;
}

/**
 * Extracts an InspectionItemAttachment from a FormGroup.
 *
 * @param attachmentFormGroup - The attachment FormGroup
 * @returns InspectionItemAttachment entity
 */
function extractAttachmentFromFormGroup(attachmentFormGroup: FormGroup<InspectionItemAttachmentFormGroup>): InspectionItemAttachment {
    return {
        id: attachmentFormGroup.controls.id.value || '',
        branchId: attachmentFormGroup.controls.branchId.value,
        tenantId: attachmentFormGroup.controls.tenantId.value,
        itemId: attachmentFormGroup.controls.itemId.value,
        createdDate: attachmentFormGroup.controls.createdDate.value,
        contentBlob: attachmentFormGroup.controls.contentBlob?.value ?? undefined,
        internalName: attachmentFormGroup.controls.internalName?.value ?? '',
        name: attachmentFormGroup.controls.name?.value ?? '',
        mimeType: attachmentFormGroup.controls.mimeType?.value ?? '',
        url: attachmentFormGroup.controls.url?.value ?? '',
        isLoading: attachmentFormGroup.controls.isLoading.value || false,
    };
}

/**
 * Extracts an InspectionItemResult from a FormGroup.
 *
 * @param resultFormGroup - The result FormGroup
 * @returns InspectionItemResult entity
 */
function extractResultFromFormGroup(resultFormGroup: FormGroup<InspectionItemResultFormGroup>): InspectionItemResult {
    return {
        id: resultFormGroup.controls.id.value as InspectionItemResultType,
        branchId: resultFormGroup.controls.branchId.value,
        tenantId: resultFormGroup.controls.tenantId.value,
        name: resultFormGroup.controls.name.value,
    };
}

/**
 * Seeds bound inspection items with the value currently held on the vehicle, and applies the same
 * constraint the server will enforce at transfer.
 *
 * Both fields are ones the technician confirms rather than looks up: showing the last known value
 * saves retyping it and lets an obviously stale one be spotted. Only empty items are seeded, so
 * reopening a part-filled inspection never overwrites what was entered.
 *
 * The odometer constraint mirrors its server rule, turning what would be a transfer-time rejection
 * into inline feedback while the technician can still fix it: the reading is bounded against the
 * stored value because it only counts up. A next service date is never bounded; a date before today
 * is an overdue service, which is a finding rather than an input error, so it derives a Fail result
 * instead (see nextServiceDateResultFor).
 *
 * @param formGroup - The inspection form group to seed, as built by createInspectionFormGroup.
 * @param vehicle - The vehicle the inspection targets, or null when it carries none.
 */
export function seedBoundItemValues(
    formGroup: FormGroup<InspectionFormGroup>,
    vehicle: Pick<Vehicle, 'mileage' | 'nextServiceDate'> | null | undefined,
): void {
    if (!vehicle) {
        return;
    }

    flattenSectionFormGroups(formGroup).forEach((section) => {
        section.controls.items.controls.forEach((item) => {
            switch (item.controls.binding.value) {
                case InspectionItemBinding.VehicleMileage:
                    seedMileage(item, vehicle.mileage);
                    break;
                case InspectionItemBinding.NextServiceDate:
                    seedNextServiceDate(item, vehicle.nextServiceDate);
                    break;
            }
        });
    });
}

function seedMileage(item: FormGroup<InspectionItemFormGroup>, mileage: number | null | undefined): void {
    const actualValue = item.get('actualValue');

    if (mileage === null || mileage === undefined || !actualValue) {
        return;
    }

    // The floor is only applied when the item can actually express it. A recorded odometer above the
    // item's own ceiling -- a genuinely high-mileage vehicle against a template whose range was set
    // too narrow -- would otherwise put the minimum above the maximum, leaving no value that
    // satisfies both.
    //
    // The value is still seeded in that case rather than skipped. The item's existing range validator
    // then reports it, so the reading and the reason it is refused are both on screen. Staying silent
    // would show a blank field and defer the problem to a transfer rejection that names neither the
    // vehicle's odometer nor the template's limit.
    const maxValue = item.get('maxValue')?.value as number | null | undefined;

    if (maxValue === null || maxValue === undefined || mileage <= maxValue) {
        actualValue.addValidators(Validators.min(mileage));
    }

    if (actualValue.value === null || actualValue.value === undefined) {
        actualValue.setValue(mileage, { emitEvent: false });
    }

    actualValue.updateValueAndValidity({ emitEvent: false });
}

/**
 * Seeds the vehicle's stored next service date into an empty item and derives the item's result from it.
 * An overdue stored date is shown rather than withheld: it is the date the vehicle was actually due,
 * and the Fail it derives is what surfaces that the vehicle is overdue at all.
 */
function seedNextServiceDate(item: FormGroup<InspectionItemFormGroup>, nextServiceDate: string | null | undefined): void {
    const dateValue = item.get('dateValue');

    if (!dateValue) {
        return;
    }

    if (nextServiceDate && (dateValue.value === null || dateValue.value === undefined || dateValue.value === '')) {
        dateValue.setValue(nextServiceDate, { emitEvent: false });
    }

    applyNextServiceDateResult(item);
}

/**
 * Result an item bound to NextServiceDate carries for a given date. A date before UTC today is an
 * overdue service, which is a finding the technician did not choose and cannot clear, so it reads as
 * Fail; today or later, no value, or an unbound item carries none. Mirrors the server rule, which
 * recomputes and stores the result on every save, so the badge on the tablet is the one the report
 * shows. UTC on both sides, because the server compares `DateTime.UtcNow.Date`.
 */
export function nextServiceDateResultFor(
    binding: InspectionItemBinding | null | undefined,
    dateValue: string | null | undefined,
): InspectionItemResultType.Fail | null {
    if (binding !== InspectionItemBinding.NextServiceDate || !dateValue) {
        return null;
    }

    const parsed = new Date(dateValue);

    if (Number.isNaN(parsed.getTime())) {
        return null;
    }

    const day = Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate());
    const now = new Date();
    const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());

    return day < today ? InspectionItemResultType.Fail : null;
}

/**
 * Stamps the derived next-service-date result onto a date item's form group. A no-op for items the
 * rule does not govern, so it is safe to call on any item after its date changes.
 */
export function applyNextServiceDateResult(item: FormGroup): void {
    const binding = item.get('binding')?.value as InspectionItemBinding | null | undefined;

    if (binding !== InspectionItemBinding.NextServiceDate) {
        return;
    }

    item.get('resultId')?.setValue(nextServiceDateResultFor(binding, item.get('dateValue')?.value as string | null), { emitEvent: false });
}

/**
 * Returns every section form group in the tree, including nested child sections, so binding-aware
 * passes reach items an inspection nests one level down.
 */
function flattenSectionFormGroups(formGroup: FormGroup<InspectionFormGroup>): FormGroup<InspectionSectionFormGroup>[] {
    const flattened: FormGroup<InspectionSectionFormGroup>[] = [];

    const visit = (sections: FormArray<FormGroup<InspectionSectionFormGroup>>) => {
        sections.controls.forEach((section) => {
            flattened.push(section);

            const children = section.get('childSections') as FormArray<FormGroup<InspectionSectionFormGroup>> | null;
            if (children) {
                visit(children);
            }
        });
    };

    visit(formGroup.controls.sections);

    return flattened;
}
